import { reactive } from 'vue'
import { apiFetch } from './api'

export interface Project {
  id: string
  name: string
  ownerUsername: string
  ownerName: string
  photoDataUrl: string
  accessMode: 'edit' | 'read'
  responsibleUsernames: string[]
  responsibleNames: string[]
  tempResponsibles: Array<{
    username: string
    startDate: string
    endDate: string
    grantedBy?: string
    createdAt?: string
  }>
  createdAt: string
}

const state = reactive({
  items: [] as Project[],
  loading: false
})

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

function mapProject(item: unknown): Project {
  const record = asRecord(item)
  const usernames = Array.isArray(record.responsibleUsernames)
    ? record.responsibleUsernames.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    : record.ownerUsername
      ? [String(record.ownerUsername)]
      : []
  const names = Array.isArray(record.responsibleNames)
    ? record.responsibleNames.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    : record.ownerName
      ? [String(record.ownerName)]
      : []
  const tempResponsibles = Array.isArray(record.tempResponsibles)
    ? record.tempResponsibles
        .filter((value): value is Record<string, unknown> => Boolean(value && typeof value === 'object'))
        .map((value) => ({
          username: String(value.username || ''),
          startDate: String(value.startDate || ''),
          endDate: String(value.endDate || ''),
          grantedBy: value.grantedBy ? String(value.grantedBy) : undefined,
          createdAt: value.createdAt ? String(value.createdAt) : undefined
        }))
    : []
  const rawPhoto = String(record.photoDataUrl || '').trim()
  const normalizedPhoto =
    !rawPhoto
      ? ''
      : rawPhoto.startsWith('data:') || rawPhoto.startsWith('http://') || rawPhoto.startsWith('https://') || rawPhoto.startsWith('/')
        ? rawPhoto
        : `data:image/jpeg;base64,${rawPhoto}`
  return {
    id: String(record.id || ''),
    name: String(record.name || ''),
    ownerUsername: String(record.ownerUsername || usernames[0] || ''),
    ownerName: String(record.ownerName || names[0] || record.ownerUsername || ''),
    photoDataUrl: normalizedPhoto,
    accessMode: record.accessMode === 'read' ? 'read' : 'edit',
    responsibleUsernames: usernames,
    responsibleNames: names,
    tempResponsibles,
    createdAt: String(record.createdAt || new Date().toISOString())
  }
}

export const projectsStore = {
  state,
  all() {
    return state.items
  },
  async refresh() {
    state.loading = true
    try {
      const rows = await apiFetch<unknown[]>('/projects')
      state.items = rows.map(mapProject)
      return state.items
    } finally {
      state.loading = false
    }
  },
  async upsert(payload: { id?: string; name: string; photoDataUrl?: string; responsibleUsernames: string[]; tempResponsibles?: Array<{ username: string; startDate: string; endDate: string; grantedBy?: string; createdAt?: string }> }) {
    await apiFetch<{ ok: boolean; id: string }>('/projects', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
    await this.refresh()
  },
  async remove(id: string) {
    await apiFetch<{ ok: boolean }>(`/projects/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    })
    await this.refresh()
  },
  getById(id: string) {
    return state.items.find((item) => item.id === id) || null
  }
}
